/**
 * frontend/src/pages/DebugPage.js
 * Real-time diagnostic dashboard for Vakya API troubleshooting.
 * Shows every API call, response, error, and timing.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './DebugPage.module.css';

const API = 'https://vlptp1rs69.execute-api.us-east-1.amazonaws.com';

export default function DebugPage() {
  const [logs, setLogs] = useState([]);
  const [query, setQuery] = useState('nature of Brahman');
  const [verseId, setVerseId] = useState('BRHUP#BrhUp_1_1.1');
  // // const [cwLogs, setCwLogs] = useState([]);
  // // const [cwLoading, setCwLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('api');
  const logsEndRef = useRef(null);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addLog = useCallback((type, label, data, ms) => {
    setLogs(prev => [{
      id: Date.now() + Math.random(),
      time: new Date().toISOString().split('T')[1].slice(0, 12),
      type,   // 'success' | 'error' | 'info' | 'warn'
      label,
      data,
      ms,
    }, ...prev]);
  }, []);

  async function runTest(label, url, options = {}) {
    const start = Date.now();
    addLog('info', `→ ${label}`, { url, body: options.body ? JSON.parse(options.body) : undefined });
    try {
      const res = await fetch(url, {
        method: options.method || 'GET',
        headers: { 'Content-Type': 'application/json', ...options.headers },
        body: options.body,
      });
      const ms = Date.now() - start;
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { json = text; }

      if (res.ok) {
        addLog('success', `✓ ${label} [${res.status}]`, json, ms);
      } else {
        addLog('error', `✗ ${label} [${res.status}]`, json, ms);
      }
      return { ok: res.ok, data: json, ms };
    } catch (err) {
      const ms = Date.now() - start;
      addLog('error', `✗ ${label} — NETWORK ERROR`, { error: err.message }, ms);
      return { ok: false, error: err.message, ms };
    }
  }

  async function testHealth() {
    await runTest('GET /health', `${API}/health`);
  }

  async function testSearch() {
    await runTest('POST /search', `${API}/search`, {
      method: 'POST',
      body: JSON.stringify({ query, page_size: 5 }),
    });
  }

  async function testSearchWithSource() {
    await runTest('POST /search (BRHUP only)', `${API}/search`, {
      method: 'POST',
      body: JSON.stringify({ query, page_size: 5, source_codes: ['BRHUP', 'CHUP', 'KATUP'] }),
    });
  }

  async function testVerse() {
    await runTest(`GET /verse/${verseId}`, `${API}/verse/${encodeURIComponent(verseId)}`);
  }

  async function testSources() {
    await runTest('GET /source', `${API}/source`);
  }

  async function runFullDiagnostic() {
    addLog('info', '═══ FULL DIAGNOSTIC START ═══', {});
    await testHealth();
    await new Promise(r => setTimeout(r, 500));
    await testSources();
    await new Promise(r => setTimeout(r, 500));
    await testSearch();
    await new Promise(r => setTimeout(r, 500));
    await testSearchWithSource();
    await new Promise(r => setTimeout(r, 500));
    await testVerse();
    addLog('info', '═══ FULL DIAGNOSTIC END ═══', {});
  }

  function clearLogs() {
    setLogs([]);
  }

  function exportLogs() {
    const text = logs.map(l =>
      `[${l.time}] [${l.type.toUpperCase()}] ${l.label}${l.ms ? ` (${l.ms}ms)` : ''}\n${JSON.stringify(l.data, null, 2)}`
    ).join('\n\n---\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `vakya-debug-${Date.now()}.txt`;
    a.click();
  }

  const successCount = logs.filter(l => l.type === 'success').length;
  const errorCount = logs.filter(l => l.type === 'error').length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.title}>वाक्य DEBUG</span>
          <span className={styles.subtitle}>API Diagnostic Dashboard</span>
        </div>
        <div className={styles.stats}>
          <span className={styles.statSuccess}>✓ {successCount}</span>
          <span className={styles.statError}>✗ {errorCount}</span>
          <span className={styles.statTotal}>{logs.length} calls</span>
        </div>
      </div>

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === 'api' ? styles.tabActive : ''}`} onClick={() => setActiveTab('api')}>API Tests</button>
        <button className={`${styles.tab} ${activeTab === 'config' ? styles.tabActive : ''}`} onClick={() => setActiveTab('config')}>Config</button>
      </div>

      {activeTab === 'api' && (
        <div className={styles.controls}>
          <div className={styles.inputRow}>
            <label>Search Query</label>
            <input
              className={styles.input}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search query..."
            />
          </div>
          <div className={styles.inputRow}>
            <label>Verse ID</label>
            <input
              className={styles.input}
              value={verseId}
              onChange={e => setVerseId(e.target.value)}
              placeholder="BRHUP#BrhUp_1_1.1"
            />
          </div>
          <div className={styles.buttonRow}>
            <button className={styles.btnPrimary} onClick={runFullDiagnostic}>â–¶ Full Diagnostic</button>
            <button className={styles.btn} onClick={testHealth}>Health</button>
            <button className={styles.btn} onClick={testSearch}>Search</button>
            <button className={styles.btn} onClick={testSearchWithSource}>Search (Upanishads)</button>
            <button className={styles.btn} onClick={testVerse}>Verse Detail</button>
            <button className={styles.btn} onClick={testSources}>Sources</button>
            <button className={styles.btnDanger} onClick={clearLogs}>Clear</button>
            <button className={styles.btn} onClick={exportLogs}>Export</button>
          </div>
        </div>
      )}

      {activeTab === 'config' && (
        <div className={styles.controls}>
          <div className={styles.configGrid}>
            <div className={styles.configItem}>
              <span className={styles.configLabel}>API Base</span>
              <span className={styles.configValue}>{API}</span>
            </div>
            <div className={styles.configItem}>
              <span className={styles.configLabel}>Lambda</span>
              <span className={styles.configValue}>vakya-api-production</span>
            </div>
            <div className={styles.configItem}>
              <span className={styles.configLabel}>DynamoDB</span>
              <span className={styles.configValue}>vakya-corpus-production</span>
            </div>
            <div className={styles.configItem}>
              <span className={styles.configLabel}>Region</span>
              <span className={styles.configValue}>us-east-1</span>
            </div>
            <div className={styles.configItem}>
              <span className={styles.configLabel}>CloudWatch</span>
              <span className={styles.configValue}>/aws/lambda/vakya-api-production</span>
            </div>
          </div>
          <div className={styles.buttonRow}>
            <button className={styles.btnPrimary} onClick={testHealth}>Test Connectivity</button>
          </div>
        </div>
      )}

      <div className={styles.logsContainer}>
        {logs.length === 0 && (
          <div className={styles.empty}>
            <span>No logs yet. Run a test above.</span>
          </div>
        )}
        {logs.map(log => (
          <div key={log.id} className={`${styles.logEntry} ${styles[`log_${log.type}`]}`}>
            <div className={styles.logHeader}>
              <span className={styles.logTime}>{log.time}</span>
              <span className={styles.logLabel}>{log.label}</span>
              {log.ms && <span className={styles.logMs}>{log.ms}ms</span>}
            </div>
            {log.data && Object.keys(log.data).length > 0 && (
              <pre className={styles.logData}>{JSON.stringify(log.data, null, 2)}</pre>
            )}
          </div>
        ))}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
}

